import { authService } from './authService';

const API_BASE_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:3000';

/**
 * Financial Reports Service - API calls for advanced financial reporting
 */
export const financialReportsService = {
    // ==================== PROFIT & LOSS ====================

    /**
     * Get P&L statement for a date range
     */
    async getProfitAndLoss(startDate, endDate) {
        try {
            const params = new URLSearchParams({
                start_date: startDate,
                end_date: endDate
            });

            const response = await fetch(`${API_BASE_URL}/financial-reports/pnl?${params}`, {
                method: 'GET',
                headers: authService.getAuthHeaders()
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to get P&L statement');
            return data;
        } catch (error) {
            console.error('Error getting P&L statement:', error);
            throw error;
        }
    },

    /**
     * Get monthly P&L statement
     */
    async getMonthlyPnL(year, month) {
        try {
            const response = await fetch(`${API_BASE_URL}/financial-reports/pnl/monthly/${year}/${month}`, {
                method: 'GET',
                headers: authService.getAuthHeaders()
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to get monthly P&L');
            return data;
        } catch (error) {
            console.error('Error getting monthly P&L:', error);
            throw error;
        }
    },

    /**
     * Get yearly P&L statement
     */
    async getYearlyPnL(year) {
        try {
            const response = await fetch(`${API_BASE_URL}/financial-reports/pnl/yearly/${year}`, {
                method: 'GET',
                headers: authService.getAuthHeaders()
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to get yearly P&L');
            return data;
        } catch (error) {
            console.error('Error getting yearly P&L:', error);
            throw error;
        }
    },

    // ==================== CASH FLOW ====================

    /**
     * Get cash flow statement for a date range
     */
    async getCashFlow(startDate, endDate) {
        try {
            const params = new URLSearchParams({
                start_date: startDate,
                end_date: endDate
            });

            const response = await fetch(`${API_BASE_URL}/financial-reports/cash-flow?${params}`, {
                method: 'GET',
                headers: authService.getAuthHeaders()
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to get cash flow statement');
            return data;
        } catch (error) {
            console.error('Error getting cash flow statement:', error);
            throw error;
        }
    },

    /**
     * Get monthly cash flow
     */
    async getMonthlyCashFlow(year, month) {
        try {
            const response = await fetch(`${API_BASE_URL}/financial-reports/cash-flow/monthly/${year}/${month}`, {
                method: 'GET',
                headers: authService.getAuthHeaders()
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to get monthly cash flow');
            return data;
        } catch (error) {
            console.error('Error getting monthly cash flow:', error);
            throw error;
        }
    },

    // ==================== TRENDS ====================

    /**
     * Get revenue trend analysis
     */
    async getRevenueTrend(months = 12) {
        try {
            const response = await fetch(`${API_BASE_URL}/financial-reports/trends/revenue?months=${months}`, {
                method: 'GET',
                headers: authService.getAuthHeaders()
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to get revenue trend');
            return data;
        } catch (error) {
            console.error('Error getting revenue trend:', error);
            throw error;
        }
    },

    /**
     * Get expense trend analysis
     */
    async getExpenseTrend(months = 12) {
        try {
            const response = await fetch(`${API_BASE_URL}/financial-reports/trends/expenses?months=${months}`, {
                method: 'GET',
                headers: authService.getAuthHeaders()
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to get expense trend');
            return data;
        } catch (error) {
            console.error('Error getting expense trend:', error);
            throw error;
        }
    },

    // ==================== ANALYSIS ====================

    /**
     * Get profitability analysis by product or category
     */
    async getProfitability(startDate, endDate, groupBy = 'product') {
        try {
            const params = new URLSearchParams({
                start_date: startDate,
                end_date: endDate,
                group_by: groupBy
            });

            const response = await fetch(`${API_BASE_URL}/financial-reports/profitability?${params}`, {
                method: 'GET',
                headers: authService.getAuthHeaders()
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to get profitability analysis');
            return data;
        } catch (error) {
            console.error('Error getting profitability analysis:', error);
            throw error;
        }
    },

    /**
     * Get payment method analysis
     */
    async getPaymentMethods(startDate, endDate) {
        try {
            const params = new URLSearchParams({
                start_date: startDate,
                end_date: endDate
            });

            const response = await fetch(`${API_BASE_URL}/financial-reports/payment-methods?${params}`, {
                method: 'GET',
                headers: authService.getAuthHeaders()
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to get payment method analysis');
            return data;
        } catch (error) {
            console.error('Error getting payment method analysis:', error);
            throw error;
        }
    },

    // ==================== EXECUTIVE SUMMARY ====================

    /**
     * Get executive summary for a year (optionally a specific month)
     */
    async getExecutiveSummary(year, month = null) {
        try {
            const params = month ? `?month=${month}` : '';
            const response = await fetch(`${API_BASE_URL}/financial-reports/executive-summary/${year}${params}`, {
                method: 'GET',
                headers: authService.getAuthHeaders()
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to get executive summary');
            return data;
        } catch (error) {
            console.error('Error getting executive summary:', error);
            throw error;
        }
    },

    /**
     * Get current month executive summary
     */
    async getCurrentMonthSummary() {
        try {
            const response = await fetch(`${API_BASE_URL}/financial-reports/executive-summary/current/month`, {
                method: 'GET',
                headers: authService.getAuthHeaders()
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to get current month summary');
            return data;
        } catch (error) {
            console.error('Error getting current month summary:', error);
            throw error;
        }
    }
};
